// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { useOrganizationAssetCurrencies } from '@/asset/hooks/use-organization-asset-currencies';
import { useCurrentUser } from '@/authentication/hooks/use-current-user';
import { IsoCurrencyCode } from '@/currency/constants/iso';

interface ActiveAssetCostCurrency {
  availableCurrencies: IsoCurrencyCode[];
  activeCurrencyCode: IsoCurrencyCode;
  isResolved: boolean;
}

/**
 * Resolves the active cost currency for a per-currency asset report: the org's
 * asset cost currencies as the available options, and the requested (URL) currency
 * clamped to one the org actually holds — falling back to the org default, then the
 * first available currency once the list loads.
 *
 * Until the currency list loads, activeCurrencyCode is provisional; isResolved says so,
 * and callers gate their snapshot fetch on it rather than requesting a large report
 * under a currency the clamp is about to change.
 */
export function useActiveAssetCostCurrency(
  requestedCurrencyCode: IsoCurrencyCode | undefined
): ActiveAssetCostCurrency {
  const { user } = useCurrentUser();
  const currenciesQuery = useOrganizationAssetCurrencies();
  const availableCurrencies = currenciesQuery.data ?? [];
  const requested = requestedCurrencyCode ?? user.organization.defaultCurrency;
  const activeCurrencyCode = availableCurrencies.includes(requested)
    ? requested
    : (availableCurrencies[0] ?? requested);
  return {
    availableCurrencies,
    activeCurrencyCode,
    isResolved: currenciesQuery.isSuccess,
  };
}
